import type {TabItem} from '@/types';

export type SidebarTabItem = TabItem & {group: string};

const getTabStateClasses = (tab: SidebarTabItem, isActive: boolean): string => {
  if (tab.isDisabled) {
    return 'cursor-not-allowed text-gray-400';
  }

  if (isActive) {
    return 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400';
  }

  return 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-dark-blueBg';
};

type SidebarTabsProps = {
  tabs: SidebarTabItem[];
  activeTabId: string;
  onTabChange: (tab: SidebarTabItem) => void;
};

const SidebarTabs = ({tabs, activeTabId, onTabChange}: SidebarTabsProps) => {
  const groups: {label: string; tabs: SidebarTabItem[]}[] = [];
  tabs.forEach((tab) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.label === tab.group) {
      lastGroup.tabs.push(tab);
    } else {
      groups.push({label: tab.group, tabs: [tab]});
    }
  });

  const currentTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];

  return (
    <div className="mt-5 flex flex-col gap-6 md:flex-row">
      <nav className="shrink-0 border-b border-gray-200 pb-4 dark:border-dark-borderMid md:w-56 md:border-b-0 md:border-r md:pb-0 md:pr-4">
        {groups.map((group, index) => (
          <div key={group.label} className={index > 0 ? 'mt-4' : undefined}>
            <p className="px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {group.label}
            </p>
            <div className="mt-1 flex flex-col gap-0.5">
              {group.tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onTabChange(tab)}
                  disabled={tab.isDisabled}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm font-medium ${getTabStateClasses(
                    tab,
                    currentTab?.id === tab.id,
                  )}`}
                >
                  {tab.title}
                </button>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="min-w-0 flex-1 overflow-x-auto">
        {currentTab?.content}
      </div>
    </div>
  );
};

export default SidebarTabs;
